import { useMemo, useState, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  isShopifyEventResource,
  resourceAuditAPI,
  shopifyEventAPI,
  type ResourceAuditAction,
  type ResourceAuditResourceType,
  type ResourceAuditTimelineItem,
  type ShopifyActorEvent,
  type ShopifyEventResource,
} from '@/services';
import { mergeAuditEntries } from '@lib/audit-merge';
import { matchShopifyActors } from '@lib/shopify-actor-match';
import { queryKeys } from '@lib/queryKeys';
import { cn } from '@resource-kit/ui';
import { shortenId } from '@resource-kit/controllers';
import type { EntityField } from '@resource-kit/controllers/react';
import { Badge, Button, LIST_TRUNCATE } from '@resource-kit/ui/react';
import { formatDateTimeCompact } from '@lib/utils';
import { webhookEventPath } from '@lib/routes';
import { ChangeSummary, ExpandedDetails, type FieldIndex } from '@components/ui/AuditChangeViews';
import {
  ChevronDown,
  Clock,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
  Store,
  User,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';

const AUDIT_PAGE_SIZE = 50;

interface AuditTimelineProps<T> {
  resourceType: ResourceAuditResourceType;
  resourceId: string;
  /**
   * The model's fields, used to project each audited value through the widget
   * that renders it everywhere else (#1804). Required rather than optional: a
   * caller that silently omitted it would print raw wire values next to a form
   * showing badges and formatted money, and nothing would fail. An empty array
   * is the honest way to say "this surface has no model".
   */
  fields: readonly EntityField<T>[];
}

interface ActionConfig {
  labelKey: string;
  icon: LucideIcon;
  badge: 'success' | 'default' | 'destructive' | 'secondary';
  dotColor: string;
}

const ACTION_CONFIG = {
  create: {
    labelKey: 'audit.created',
    icon: Plus,
    badge: 'success',
    dotColor: 'bg-success/10 text-success',
  },
  update: {
    labelKey: 'audit.updated',
    icon: Pencil,
    badge: 'default',
    dotColor: 'bg-primary/10 text-primary',
  },
  delete: {
    labelKey: 'audit.deleted',
    icon: Trash2,
    badge: 'destructive',
    dotColor: 'bg-destructive/10 text-destructive',
  },
  restore: {
    labelKey: 'audit.restored',
    icon: RotateCcw,
    badge: 'secondary',
    dotColor: 'bg-secondary text-muted-foreground',
  },
} as const satisfies Record<ResourceAuditAction, ActionConfig>;

function formatRelativeTime(timestamp: string): string {
  const date = new Date(timestamp);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHr = Math.floor(diffMs / 3_600_000);
  const diffDay = Math.floor(diffMs / 86_400_000);

  if (diffMin < 1) return i18n.t('audit.justNow');
  if (diffMin < 60) return i18n.t('audit.minutesAgo', { count: diffMin });
  if (diffHr < 24) return i18n.t('audit.hoursAgo', { count: diffHr });
  if (diffDay < 7) return i18n.t('audit.daysAgo', { count: diffDay });
  return date.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' });
}

interface ActorLabel {
  /** The rendered text, already carrying the shortened event id if any. */
  label: string;
  /**
   * The webhook event this row came from, or null when there is nothing to
   * open. Non-null ONLY for the two webhook sources with an event recorded:
   * `manual` / `cron` / `backfill` / `smoke` and every user actor carry no
   * event, and linking them would fabricate a dead route.
   *
   * A structured return rather than JSX on purpose: the label is also consumed
   * as plain text, and returning a node would force the caller to keep a
   * second, text-only copy of the same decision in sync.
   */
  eventId: string | null;
}

/**
 * `eventId` is the internal `webhook_events` primary key — the ingest path
 * (`platform/webhook/ingest.ts`) records the inserted row's `id`, never the
 * provider's `provider_event_id`. So `/webhooks/${eventId}` is a legal route
 * with no shape check and no lookup, and it stays legal past the 90-day
 * webhook retention: that job blanks `raw_payload` with an UPDATE, it does not
 * delete the row.
 */
function resolveActorLabel(entry: ResourceAuditTimelineItem): ActorLabel {
  if (entry.actorType === 'user') {
    return {
      label: entry.actorLabel || entry.actorId || i18n.t('audit.actor.unknown'),
      eventId: null,
    };
  }
  if (entry.source === 'shopify_webhook') {
    return entry.eventId
      ? {
          label: i18n.t('audit.actor.shopifyWebhookWithEvent', {
            event: shortenId(entry.eventId, LIST_TRUNCATE).text,
          }),
          eventId: entry.eventId,
        }
      : { label: i18n.t('audit.actor.shopifyWebhook'), eventId: null };
  }
  if (entry.source === 'onfleet_webhook') {
    return entry.eventId
      ? {
          label: i18n.t('audit.actor.onfleetWebhookWithEvent', {
            event: shortenId(entry.eventId, LIST_TRUNCATE).text,
          }),
          eventId: entry.eventId,
        }
      : { label: i18n.t('audit.actor.onfleetWebhook'), eventId: null };
  }
  if (entry.source === 'manual')
    return { label: i18n.t('audit.actor.manualUnattributed'), eventId: null };
  if (entry.source === 'cron') return { label: i18n.t('audit.actor.cron'), eventId: null };
  if (entry.source === 'backfill') return { label: i18n.t('audit.actor.backfill'), eventId: null };
  if (entry.source === 'smoke') return { label: i18n.t('audit.actor.smoke'), eventId: null };
  return { label: i18n.t('audit.actor.system'), eventId: null };
}

/**
 * Who caused this change. A SIBLING of the expand button, never a child of it:
 * an `<a>` inside a `<button>` is illegal nesting and makes one click both
 * navigate and toggle (the #1869 trap — a cell that carries its own control
 * cannot be wrapped in one).
 *
 * No permission branch, deliberately. `/webhooks/:id` is behind `AdminGuard`
 * while this panel needs `audit:read`, which sits in the backend's
 * `NON_ADMIN_EXCLUDED` set and is carried by no non-admin preset — so every
 * operator who can see this chip today is an admin.
 */
function ActorChip({ entry }: { entry: ResourceAuditTimelineItem }) {
  const { t } = useTranslation();
  const { label, eventId } = resolveActorLabel(entry);
  const className = cn(
    'inline-flex items-center gap-1 text-xs min-w-0 flex-1',
    entry.actorType === 'user' ? 'font-medium text-foreground' : 'text-muted-foreground',
  );
  const title = entry.actorId ?? t('audit.actor.systemHint');
  const body = (
    <>
      <User className="w-3 h-3 shrink-0" aria-hidden="true" />
      <span className="sr-only">{t('audit.actor.srLabel')}: </span>
      <span className="truncate">{label}</span>
    </>
  );

  if (eventId === null) {
    return (
      <span className={className} title={title}>
        {body}
      </span>
    );
  }
  return (
    <Link
      to={webhookEventPath(eventId)}
      title={title}
      // Defensive: the toggle is a sibling today, but any ancestor row handler
      // added later must not fire on a navigation click.
      onClick={(event: MouseEvent<HTMLAnchorElement>) => event.stopPropagation()}
      className={cn(
        className,
        'hover:text-accent hover:underline underline-offset-2 transition-colors',
      )}
    >
      {body}
    </Link>
  );
}

/**
 * Who Shopify says did this, when the operator has asked and the timing lines
 * up. Rendered NEXT TO the webhook actor chip rather than replacing it: the
 * two answer different questions — the chip says which integration wrote the
 * row, this says which person acted on Shopify — and collapsing them would
 * make a webhook look like it was performed by a human.
 */
function ShopifyActorChip({ events }: { events: readonly ShopifyActorEvent[] }) {
  const { t } = useTranslation();
  return (
    <span
      className="inline-flex items-center gap-1 text-xs text-foreground font-medium shrink-0"
      title={events.map((event) => event.description ?? event.verb).join('\n')}
    >
      <Store className="w-3 h-3 shrink-0" aria-hidden="true" />
      <span className="sr-only">{t('audit.shopifyActor.srLabel')}: </span>
      {events.map((event) => event.actor).join(', ')}
    </span>
  );
}

function TimelineEntry<T>({
  entry,
  fieldIndex,
  shopifyActors,
}: {
  entry: ResourceAuditTimelineItem;
  fieldIndex: FieldIndex<T>;
  shopifyActors?: readonly ShopifyActorEvent[] | undefined;
}) {
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();
  const config = ACTION_CONFIG[entry.action];
  const Icon = config.icon;
  const hasDetails =
    entry.changes.length > 0 ||
    Object.keys(entry.newValues ?? {}).length > 0 ||
    Object.keys(entry.oldValues ?? {}).length > 0;
  const absoluteTime = formatDateTimeCompact(entry.createdAt);
  const relativeTime = formatRelativeTime(entry.createdAt);

  return (
    <div className="relative flex gap-3 pb-6 last:pb-0 group">
      <div className="absolute left-[15px] top-8 bottom-0 w-px bg-border group-last:hidden" />
      <div
        className={cn(
          'relative z-10 flex items-center justify-center w-8 h-8 rounded-full shrink-0',
          config.dotColor,
        )}
      >
        <Icon className="w-3.5 h-3.5" aria-hidden="true" />
      </div>
      <div className="flex-1 min-w-0 pt-0.5">
        <div className="flex items-center gap-2">
          {/*
            The toggle wraps the action badge and its chevron only. It used to
            wrap the whole row, which is what made the actor chip unlinkable —
            and what let a widget-rendered value in the summary put a control
            inside a control.
          */}
          <button
            type="button"
            className={cn(
              'flex items-center gap-2 shrink-0 rounded-md px-2 py-1 -mx-2 -my-1 text-left transition-colors',
              hasDetails ? 'hover:bg-muted cursor-pointer' : 'cursor-default',
            )}
            onClick={() => hasDetails && setExpanded(!expanded)}
            aria-expanded={hasDetails ? expanded : undefined}
          >
            <Badge variant={config.badge} className="text-[10px] uppercase px-1.5 py-0 shrink-0">
              {t(config.labelKey)}
            </Badge>
            {hasDetails && (
              <ChevronDown
                className={cn(
                  'w-3.5 h-3.5 text-muted-foreground/40 transition-transform shrink-0',
                  expanded && 'rotate-180',
                )}
              />
            )}
          </button>
          <ActorChip entry={entry} />
          {shopifyActors !== undefined && <ShopifyActorChip events={shopifyActors} />}
          <span
            className="inline-flex items-center gap-1 text-xs text-muted-foreground/80 shrink-0 font-mono ml-auto"
            title={`${absoluteTime} · ${relativeTime}`}
          >
            <Clock className="w-3 h-3" aria-hidden="true" />
            <span className="sr-only">{t('audit.time.srLabel')}: </span>
            <span className="tabular-nums">{absoluteTime}</span>
            <span className="text-muted-foreground/50">· {relativeTime}</span>
          </span>
        </div>
        <div className="mt-1 text-sm text-muted-foreground break-words">
          <ChangeSummary entry={entry} fieldIndex={fieldIndex} />
        </div>
        {expanded && <ExpandedDetails entry={entry} fieldIndex={fieldIndex} />}
      </div>
    </div>
  );
}

export function AuditTimeline<T>({ resourceType, resourceId, fields }: AuditTimelineProps<T>) {
  const { t } = useTranslation();
  const fieldIndex = useMemo<FieldIndex<T>>(
    () => new Map(fields.map((field) => [String(field.key), field])),
    [fields],
  );
  const query = useInfiniteQuery({
    queryKey: queryKeys.audit.forResource(resourceType, resourceId),
    queryFn: ({ pageParam }) =>
      resourceAuditAPI.list({
        resource_type: resourceType,
        resource_id: resourceId,
        size: AUDIT_PAGE_SIZE,
        page: pageParam,
      }),
    initialPageParam: 1,
    /**
     * Offset paging, deliberately: the endpoint's envelope is
     * `{items, total, page, size}` and its only sortable column is
     * `created_at`, which has no tie-break — a cursor would need a backend
     * change. Known trade-off: rows written WHILE an operator pages can shift
     * the window, so a row may repeat across two pages. The ledger is
     * append-only at the top and a page is 50 rows, so the shift is small and
     * never hides a row that was already loaded.
     */
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((count, page) => count + page.items.length, 0);
      return loaded < lastPage.total ? lastPage.page + 1 : undefined;
    },
    staleTime: 30_000,
  });
  /**
   * The loaded ROWS, composed into the entries an operator should read: one
   * Shopify webhook writes two ledger rows and they are one edit (#2147).
   *
   * Memoised, not inline: `mergeAuditEntries` hands back the original object
   * for anything it does not merge, and `ChangeSummary` memoises on
   * `entry.changes[0]` identity — a list rebuilt every render would re-parse a
   * multi-KB payload per row, per render. Paging is untouched above: it counts
   * raw rows, so a shorter rendered list never decides "is there a next page".
   */
  const entries = useMemo(
    () => mergeAuditEntries(query.data?.pages.flatMap((page) => page.items) ?? []),
    [query.data],
  );

  // The Shopify actor lookup is OPT-IN per panel: one outbound Admin call per
  // press, so it must never fire on render. `asked` gates the query rather
  // than a manual `refetch()`, so the result stays in the cache and a second
  // press is free.
  const [asked, setAsked] = useState(false);
  const canAskShopify = isShopifyEventResource(resourceType);
  const shopifyQuery = useQuery({
    queryKey: queryKeys.audit.shopifyActors(resourceType, resourceId),
    queryFn: () =>
      shopifyEventAPI.lookup({
        // Narrowed by `canAskShopify`, which also gates `enabled`.
        resource_type: resourceType as ShopifyEventResource,
        resource_id: resourceId,
      }),
    enabled: asked && canAskShopify,
    staleTime: 300_000,
  });
  const shopifyActors = useMemo(
    () => matchShopifyActors(entries, shopifyQuery.data?.events ?? []),
    [entries, shopifyQuery.data],
  );

  if (query.isLoading) {
    return (
      <div className="space-y-4 animate-pulse">
        {[1, 2, 3].map((index) => (
          <div key={index} className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-secondary" />
            <div className="flex-1 space-y-2 pt-1">
              <div className="h-4 bg-secondary rounded w-3/4" />
              <div className="h-3 bg-secondary rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (query.error)
    return <div className="text-sm text-destructive py-4">{t('audit.loadFailed')}</div>;
  // `&& !query.hasNextPage` because merging can now empty the rendered list
  // while history remains: a group whose rows compose back to where they
  // started is dropped, and without this guard the early return would take the
  // Load More button down with it.
  if (entries.length === 0 && !query.hasNextPage) {
    return (
      <div className="text-sm text-muted-foreground py-4 text-center">{t('audit.noChanges')}</div>
    );
  }

  return (
    <div className="relative">
      {canAskShopify && (
        <div className="mb-4 flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-border pb-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAsked(true)}
            disabled={shopifyQuery.isFetching}
          >
            {shopifyQuery.isFetching
              ? t('audit.shopifyActor.loading')
              : t('audit.shopifyActor.ask')}
          </Button>
          {/*
            Stated BEFORE the operator clicks, not after. Shopify emits events
            for lifecycle actions only, so a price or title edit comes back
            unattributed — and an unattributed row reads as a broken feature
            unless the boundary was named up front.
          */}
          <span className="text-xs text-muted-foreground">{t('audit.shopifyActor.scope')}</span>
          {shopifyQuery.error !== null && (
            <span className="text-xs text-destructive">{t('audit.shopifyActor.failed')}</span>
          )}
        </div>
      )}
      {entries.map((entry) => (
        <TimelineEntry
          key={entry.id}
          entry={entry}
          fieldIndex={fieldIndex}
          shopifyActors={shopifyActors.get(entry.id)}
        />
      ))}
      {query.hasNextPage ? (
        <div className="pt-2 text-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
          >
            {query.isFetchingNextPage ? t('audit.loadingMore') : t('audit.loadMore')}
          </Button>
        </div>
      ) : (
        entries.length > AUDIT_PAGE_SIZE && (
          <div className="text-xs text-muted-foreground/60 text-center pt-2">
            {t('audit.showingAll', { count: entries.length })}
          </div>
        )
      )}
    </div>
  );
}
